import { FC, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import NewsCard from "./components/NewsCard";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { selectNews } from "../../store/slices/news";
import {
  getNewsPagination,
  NEWS_PAGE_SIZE,
} from "../../api/asyncThuncks/newsCards";
import Button from "../button/Button";
import EmptyContentList from "../emptyContent/components/EmptyContentList";
const loadingGif = require("../../assets/loadingGif.gif") as string;

type IProps = {
  activeTab?: string;
};

const NewsList: FC<IProps> = ({ activeTab }) => {
  const dispatch = useAppDispatch();
  const { news, total, page, paginationLoading, paginationError } =
    useAppSelector(selectNews);
  const { pathname } = useLocation();
  const requestRef = useRef<{ abort: () => void } | null>(null);
  const isNewsRoute = /^\/news(?:\/|$)/.test(pathname);
  const isVideo =
    activeTab === "textNews" ? false : activeTab === "videoNews" ? true : undefined;
  const publicNews = news.filter((card) => card.isPublic === true);
  const displayedNews = isNewsRoute ? publicNews : publicNews.slice(0, 8);
  const isPending = paginationLoading === "pending";
  const isInitialError = !!paginationError && page === 0;
  const hasMore = total !== null && page * NEWS_PAGE_SIZE < total;

  const loadNews = useCallback(
    (nextPage: number) => {
      requestRef.current = dispatch(
        getNewsPagination({ page: nextPage, isVideo })
      );
    },
    [dispatch, isVideo]
  );

  useEffect(() => {
    loadNews(1);
    return () => requestRef.current?.abort();
  }, [loadNews, pathname]);

  const handleLoadMore = () => {
    if (!isPending) loadNews(page + 1);
  };

  return (
    <>
      {isPending && news.length === 0 ? (
        <div className="w-full h-full flex justify-center items-center bg-white z-50">
          <img
            className="max-w-[50px] max-h-[50px]"
            src={loadingGif}
            alt="loading..."
          />
        </div>
      ) : isInitialError ? (
        <EmptyContentList />
      ) : (
        <div className="flex flex-col items-start md:flex-row md:justify-stretch md:items-start md:flex-wrap md:w-[100%]">
          {displayedNews.map((card) => (
            <NewsCard
              key={card.id}
              id={card.id}
              icon={card.imagePath}
              title={card.title}
              description={card.description}
              date={card.publishedAt}
              link={card.publicUrl}
              metaDescription={card.metaDescription}
              youtubeUrl={card.youtubeUrl}
            />
          ))}
        </div>
      )}
      {isNewsRoute && (hasMore || isInitialError) && (
        <div className="w-full pt-5 md:pt-0">
          {paginationError && !isInitialError && (
            <p role="alert" className="pt-5 text-center">
              Не вдалося завантажити новини. Спробуйте ще раз.
            </p>
          )}
          <div className="w-full flex items-center justify-center">
            <Button
              onClick={handleLoadMore}
              disabled={isPending}
              title={
                isPending
                  ? "Завантажуємо..."
                  : isInitialError
                  ? "Повторити"
                  : "Показати більше"
              }
              className="mt-5 bg-light_gray md:bg-light_gray md:bg-opacity-30 transition-opacity hover:opacity-80 hover:transition-opacity"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default NewsList;
